import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/userSlice';
import { clearAuthData } from '../../utils/localStorage';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CText, CHeader } from '../../components/common';
import { FiUser, FiShield, FiClock, FiCreditCard, FiLogOut, FiEdit, FiChevronRight, FiSettings, FiCalendar, FiDollarSign, FiStar, FiAward } from 'react-icons/fi';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const isExpert = user?.is_expert || false;

  const expertMenuItems = {
    section: 'Expert Dashboard',
    items: [
      { icon: FiSettings, title: 'Expert Settings', subtitle: 'Manage your expert profile', route: routes.expertSettings },
      { icon: FiCalendar, title: 'Manage Availability', subtitle: 'Set your available time slots', route: routes.expertAvailability },
      { icon: FiDollarSign, title: 'Session Rates', subtitle: 'Set your consultation rates', route: routes.expertRates },
      { icon: FiStar, title: 'Specializations', subtitle: 'Manage your areas of expertise', route: routes.expertSpecializations },
      { icon: FiCreditCard, title: 'Wallet & Payouts', subtitle: 'View earnings and payouts', route: routes.expertWallet },
    ],
  };

  const accountMenuItems = {
    section: strings.accountDetails,
    items: [
      { icon: FiUser, title: strings.personalDetails, subtitle: strings.yourAccountInformation, route: routes.personalDetails },
      { icon: FiShield, title: strings.identifyVerification, subtitle: strings.yourVerificationStatus },
      { icon: FiClock, title: strings.transactionHistory, subtitle: strings.yourTransactionDetails },
      { icon: FiCreditCard, title: strings.bankAccount, subtitle: strings.manageYourBankAccount, route: routes.bankAccount },
    ],
  };

  const menuSections = isExpert
    ? [expertMenuItems, accountMenuItems]
    : [accountMenuItems];

  const RightAccessory = () => (
    <div className="mr-2.5 cursor-pointer" onClick={() => navigate(routes.personalDetails)}>
      {React.createElement(FiEdit as any, { size: 20, color: colors.white })}
    </div>
  );

  const renderMenuItem = (item: { icon: any; title: string; subtitle: string; route?: string }) => (
    <div
      key={item.title}
      className="h-16 md:h-18 rounded-xl flex justify-between items-center my-2 px-3 cursor-pointer hover:opacity-80 transition-opacity border"
      style={{
        borderColor: colors.dark ? colors.grayScale700 : colors.grayScale200,
      }}
      onClick={() => item.route && navigate(item.route)}
    >
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: colors.inputBackground }}
        >
          {React.createElement(item.icon as any, { size: 20, color: colors.primary })}
        </div>
        <div>
          <CText type="B12" color={colors.textColor}>{item.title}</CText>
          <CText type="R10" color={colors.grayScale500}>{item.subtitle}</CText>
        </div>
      </div>
      {React.createElement(FiChevronRight as any, { size: 20, color: colors.grayScale500 })}
    </div>
  );

  return (
    <div
      className="min-h-screen pb-28 lg:pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-5xl lg:mx-auto">
      {/* Profile Header */}
      <div
        className="h-64 md:h-72 mt-2 flex flex-col items-center"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
        }}
      >
        <CHeader rightIcon={<RightAccessory />} color={colors.white} />

        {/* Profile Avatar */}
        {user?.image ? (
          <img
            src={user.image}
            alt="Profile"
            className="w-20 h-20 md:w-24 md:h-24 rounded-full mt-6 object-cover"
          />
        ) : (
          <div
            className="w-20 h-20 md:w-24 md:h-24 rounded-full mt-6 flex items-center justify-center text-3xl md:text-4xl font-bold"
            style={{
              background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
              color: colors.white,
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}

        {/* User Info */}
        <div className="mt-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <CText type="B20" color={colors.white}>
              {user?.name || 'User Name'}
            </CText>
            {isExpert && (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: '#f59e0b', color: '#fff' }}
              >
                Expert
              </span>
            )}
          </div>
          <CText type="R14" color={colors.primary2}>
            {user?.email || 'user@example.com'}
          </CText>
          {user?.phone_number && (
            <CText type="R12" color={colors.primary2}>
              {user.phone_number}
            </CText>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 lg:px-8">
        {/* Become an Expert Banner (for regular users) */}
        {!isExpert && (
          <div
            className="rounded-xl flex justify-between items-center mt-4 overflow-hidden border cursor-pointer hover:opacity-90 transition-opacity"
            style={{ borderColor: '#f59e0b60', backgroundColor: '#f59e0b08' }}
            onClick={() => navigate(routes.personalDetails)}
          >
            <div className="p-4 flex-1">
              <CText type="B14" color={colors.textColor}>
                Become a Financial Expert
              </CText>
              <CText type="r12" color={colors.grayScale500} style={{ marginTop: '2px' }}>
                Share your expertise and earn money on Yantrai
              </CText>
            </div>
            <div
              className="h-full w-16 flex items-center justify-center p-4"
              style={{ backgroundColor: '#f59e0b15' }}
            >
              {React.createElement(FiAward as any, { size: 24, color: '#f59e0b' })}
            </div>
          </div>
        )}

        {/* Invitation Banner */}
        <div
          className="h-20 rounded-xl flex justify-between items-center mt-4 overflow-hidden border"
          style={{ borderColor: colors.primary2 }}
        >
          <CText type="R14" style={{ width: '55%', marginLeft: '20px', lineHeight: '1.5' }}>
            Invite friends and earn rewards together!
          </CText>
          <div
            className="h-20 w-24 flex items-center justify-center text-3xl rounded-br-xl"
            style={{ backgroundColor: colors.primary50 }}
          >
            🎁
          </div>
        </div>

        {/* Menu Sections */}
        {menuSections.map((section) => (
          <div key={section.section} className="mb-6">
            <CText type="B16" color={colors.textColor} style={{ marginTop: '16px', marginBottom: '12px' }}>
              {section.section}
            </CText>
            {section.items.map(renderMenuItem)}
          </div>
        ))}

        {/* Logout Button */}
        <div
          className="h-16 rounded-xl flex justify-between items-center mt-6 px-3 cursor-pointer hover:opacity-80 transition-opacity border"
          style={{
            backgroundColor: colors.alertColor + '10',
            borderColor: colors.alertColor,
          }}
          onClick={() => { clearAuthData(); dispatch(logout()); navigate(routes.login); }}
        >
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.alertColor + '20' }}
            >
              {React.createElement(FiLogOut as any, { size: 20, color: colors.alertColor })}
            </div>
            <CText type="M14" color={colors.alertColor}>{strings.logOut}</CText>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Profile;
